"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubscriptionStatusOrdered = exports.PaymentPlanCategoriesOrdered = exports.EntitlementCompareFunctions = exports.DefaultEntitlementValues = exports.EntitlementWorkspaceBehaviors = exports.EntitlementTypes = void 0;
const models_1 = require("@buildwithflux/models");
/**
 * The list of entitlement types we currently support
 *
 * To add a new type of entitlement (e.g. a new static list of values) add it here first, then you should be prompted to
 * add support for it in ../models/compare.ts
 */
exports.EntitlementTypes = [
    "boolean",
    "largestNumber",
    "largestFiniteNumber",
    "planCategory",
    "copilotPaidCreditPriceType",
    "creditAmount",
    "spendingLimit",
];
/**
 * The list of workspace behaviors we support for entitlements
 *
 * When a user has entitlements coming from both their user plans and a workspace's plans, the workspace behavior
 * determines - per entitlement - how those values are combined.
 */
exports.EntitlementWorkspaceBehaviors = [
    /**
     * Override means the workspace value for the entitlement will be used in preference to any value from the user's
     * plans
     */
    "override",
    /**
     * Merge means the normal merging behavior (see EntitlementCompareFunctions) will be used to combine the entitlement
     * value from the user plans and the workspace plans
     */
    "merge",
];
exports.DefaultEntitlementValues = {
    boolean: false,
    largestNumber: 0,
    largestFiniteNumber: 0,
    planCategory: models_1.PaymentPlanCategory.enum.userNoPlan,
    creditAmount: models_1.CreditAmount.parse(0),
    spendingLimit: models_1.SpendingLimit.parse(0),
    copilotPaidCreditPriceType: "default",
};
exports.EntitlementCompareFunctions = {
    boolean: (a, b) => (a.value ? 1 : b.value ? -1 : 0),
    largestNumber: (a, b) => {
        if (a.value === "infinity" && b.value === "infinity") {
            return 0;
        }
        if (a.value === "infinity") {
            return 1;
        }
        if (b.value === "infinity") {
            return -1;
        }
        return a.value - b.value;
    },
    largestFiniteNumber: (a, b) => a.value - b.value,
    spendingLimit: (a, b) => a.value - b.value,
    creditAmount: (a, b) => a.value - b.value,
    /**
     * The plan category sort method is used to sort the "category" entitlement, and is a bit of a special case.
     *
     * This is because we often look up the "category" entitlement with the "includeInactivePlans" mode enabled, which
     * allows us to see what plans the user might have with payment issues (i.e. they don't contribute to normal
     * entitlements, but we want to be able to prompt the user to fix their issue).
     *
     * Therefore, the top-sorted plan category according to the below sort function is not necessarily the "actual"
     * current plan category for the account (because e.g. it might be currently past_due and not contributing to other
     * entitlements).
     */
    planCategory: (a, b) => {
        /*
         * First, any plan category always wins over no plan even if it has an inactive status.
         *
         * This is because otherwise userNoPlan and userLegacyFree are _always_ status=active, and would otherwise
         * sort to the above, meaning we couldn't use includeInactivePlans=true to get the most salient plan (e.g. to
         * show payment issues on a pro plan that's inactive for the purposes of getting normal entitlements)
         *
         * The key idea here is that this planCategory sorting happens _before_ we knock out any inactive plans - so
         * it's OK if we put inactive plans above always-active plans, as long as they're more salient for the
         * includeInactivePlans=true mode.
         */
        if (a.value === models_1.PaymentPlanCategory.enum.userNoPlan || a.value === models_1.PaymentPlanCategory.enum.userLegacyFree) {
            return -1;
        }
        if (b.value === models_1.PaymentPlanCategory.enum.userNoPlan || b.value === models_1.PaymentPlanCategory.enum.userLegacyFree) {
            return 1;
        }
        // Next, any status wins over no status
        if (a.status === undefined) {
            return -1;
        }
        if (b.status === undefined) {
            return 1;
        }
        // Next, more active statuses win over less active statuses
        if (exports.SubscriptionStatusOrdered[a.status] !== exports.SubscriptionStatusOrdered[b.status]) {
            return exports.SubscriptionStatusOrdered[a.status] - exports.SubscriptionStatusOrdered[b.status];
        }
        // If there's more than one active plan, the higher category wins
        return exports.PaymentPlanCategoriesOrdered[a.value] - exports.PaymentPlanCategoriesOrdered[b.value];
    },
    /**
     * The copilotPaidCreditPriceType entitlement is currently either "default" or "discount"
     *
     * The sorting logic for copilotPaidCreditPriceType ensures that "discount" always precedes "default".
     * This is because "discount" represents a special pricing tier that should take precedence over the standard
     * "default" pricing tier when both are present.
     */
    copilotPaidCreditPriceType: function (a, b) {
        if ((a.value === "discount" && b.value === "discount") || (a.value === "default" && b.value === "default")) {
            return 0;
        }
        if (a.value === "discount") {
            return -1;
        }
        return 1;
    },
};
/**
 * The payment plan categories mapped into a numeric ordering
 *
 * The higher numbered categories "win" in an entitlement merge
 *
 * In general:
 *  - Categories that represent the absence of a plan must be mapped lower
 *  - Legacy plans are mapped higher than the plans they replace: when a user has a new plan they generally won't have
 *     a legacy plan contributing to the merge in any case, whereas legacy users may legitimately have either/both, and
 *     their legacy plan will win until they lose it.
 *  - Higher "levels" of subscription win over lower levels (like pro wins over starter)
 *  - Any paid user plan wins over any team plan: note that this is _NOT_ saying that's true for our new model: mapping
 *     plan category as an entitlement is _explicitly_ about translating legacy entitlements into the new model. No new
 *     feature will be using category as a "proxy" for entitlements: it should be directly asking about the
 *     entitlements it cares about!
 *  - organizationEnterprise wins over enterprise because it's more specific
 */
exports.PaymentPlanCategoriesOrdered = {
    [models_1.PaymentPlanCategory.enum.userNoPlan]: 0,
    [models_1.PaymentPlanCategory.enum.userLegacyFree]: 1,
    [models_1.PaymentPlanCategory.enum.organizationFree]: 10,
    [models_1.PaymentPlanCategory.enum.organizationPaid]: 11,
    [models_1.PaymentPlanCategory.enum.enterprise]: 12,
    [models_1.PaymentPlanCategory.enum.organizationEnterprise]: 13,
    [models_1.PaymentPlanCategory.enum.organizationManaged]: 14,
    [models_1.PaymentPlanCategory.enum.userEdu]: 20,
    [models_1.PaymentPlanCategory.enum.userLegacyEdu]: 21,
    [models_1.PaymentPlanCategory.enum.userStarter]: 30,
    [models_1.PaymentPlanCategory.enum.userCore]: 31,
    [models_1.PaymentPlanCategory.enum.userPro]: 32,
    [models_1.PaymentPlanCategory.enum.userLegacyEarly]: 40,
    [models_1.PaymentPlanCategory.enum.userLegacyPro]: 41,
    [models_1.PaymentPlanCategory.enum.userLegacyUltra]: 42,
};
/**
 * The subscription statuses mapped into a numeric ordering
 *
 * The higher-numbered statuses "win" in an entitlement merge
 *
 * - First canceled plans
 * - Then plans with payment issues
 * - Then "trialing"
 * - Then both "active" and "incomplete" at the same sort order
 *   - "incomplete" isn't a payment issue, just a delayed payment mode, and should be treated exactly the same as active
 */
exports.SubscriptionStatusOrdered = {
    [models_1.SubscriptionStatus.enum.canceled]: 0,
    [models_1.SubscriptionStatus.enum.past_due]: 10,
    [models_1.SubscriptionStatus.enum.unpaid]: 11,
    [models_1.SubscriptionStatus.enum.incomplete_expired]: 12,
    [models_1.SubscriptionStatus.enum.paused]: 13,
    [models_1.SubscriptionStatus.enum.trialing]: 30,
    [models_1.SubscriptionStatus.enum.incomplete]: 40,
    [models_1.SubscriptionStatus.enum.active]: 40,
};
//# sourceMappingURL=type.js.map